'use client';

import React from 'react';
import { redirect, useParams } from 'next/navigation';
import Main from '@/components/ui/layout/main';
import { capitalizeFirstCharactersForEachWord } from '@/lib/utils';
import ProjectGalleryProvider from '@/components/providers/ProjectGalleryProvider';
import { OurBuildRenovateProcess } from '@/components/ui/sections/OurBuildRenovateProcess';
import { URLS } from '@/lib/consts';
import { ContactUsSection } from '../../floor-plans/[floor-plan-name]/ui/ContactUsSection';
import { PROJECT_NAMES } from '../consts';
import type { UrlProjectName, ProjectName } from '../types';
import { PROJECT_DATA_MAP } from './data';
import { ProjectHero } from './ui/ProjectHero';
import { ProjectsDetailsSection } from './ui/ProjectsDetailsSection';
import { ProjectsGallerySection } from './ui/ProjectsGallerySection';
import { ProjectStorySection } from './ui/ProjectStorySection';
import { ProjectFeaturesSection } from './ui/ProjectFeaturesSection';
import { ProjectDesignSection } from './ui/ProjectDesignSection';
import { ProjectNeighborhoodSection } from './ui/ProjectNeighborhoodSection';
import { ProjectFAQSection } from './ui/ProjectFAQSection';
import { ProjectTestimonialSection } from './ui/ProjectTestimonialSection';
import { RelatedProjectsSection } from './ui/RelatedProjectsSection';
import { ProjectStructuredData } from './ui/StructuredData';

function Page() {
  const { name } = useParams<{ name: UrlProjectName }>();
  const formattedProjectName = capitalizeFirstCharactersForEachWord(
    name,
    '-',
  ) as ProjectName;

  if (!PROJECT_NAMES.includes(name)) {
    redirect(URLS.PROJECTS);
  }

  const projectData = PROJECT_DATA_MAP[name];

  return (
    <ProjectGalleryProvider>
      <ProjectStructuredData project={projectData} />
      <ProjectHero project={projectData} />
      <Main padding="py-0 px-0" sm="" md="" lg="">
        <ProjectsGallerySection projectName={formattedProjectName} />
        <ProjectsDetailsSection projectName={formattedProjectName} />
        <ProjectStorySection
          storyTitle={projectData.storyTitle}
          storyParagraphs={projectData.storyParagraphs}
        />
        <ProjectFeaturesSection
          features={projectData.features}
          projectName={projectData.name}
        />
        <ProjectDesignSection
          designDetails={projectData.designDetails}
          projectName={projectData.name}
        />
        <ProjectNeighborhoodSection
          neighborhood={projectData.neighborhood}
          projectName={projectData.name}
        />
        <ProjectFAQSection
          faqs={projectData.faqs}
          projectName={projectData.name}
        />
        {projectData.testimonial && (
          <ProjectTestimonialSection
            testimonial={projectData.testimonial}
            projectName={projectData.name}
          />
        )}
        <ContactUsSection />
        <OurBuildRenovateProcess />
        <RelatedProjectsSection
          relatedProjects={projectData.relatedProjects}
        />
      </Main>
    </ProjectGalleryProvider>
  );
}

export default Page;
